import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/app/Header";
import FeeAlertBanner from "@/components/app/FeeAlertBanner";
import OutlookReconnectBanner from "@/components/app/OutlookReconnectBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import BulkActionBar from "@/components/app/BulkActionBar";
// EdrsOutstandingWidget removed from the dashboard on user request.
// Import intentionally dropped so unused-import lint doesn't complain.
import {
  Plus,
  FolderSimple,
  ArrowRight,
  Clock,
  CheckCircle,
  House,
  MagnifyingGlass,
  ListChecks,
  Warning,
  Archive,
  Trash,
  Handshake,
  ArrowClockwise,
  User,
  Kanban,
  Tray,
  Calculator,
  Receipt,
  ChartBar,
  Users,
  FileText,
  Scales,
  NotePencil,
  Stamp,
  ShieldCheck,
  SquaresFour,
  Rows,
  Signature,
  CaretRight,
} from "@phosphor-icons/react";


// ─────────────────── Firm-wide Safe Harbour tile ────────────────────
// Renders on the solicitor home page — shows the MLRO the current
// state of NFC chip reads across every live case. Chip-read % is the
// number that HMLR / SRA auditors actually ask for during a Practice
// Guide 8 compliance inspection.
function SafeHarbourTile() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [ocr, setOcr] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [onlyOverrides, setOnlyOverrides] = useState(false);
  const navigate = useNavigate();

  const openOcr = async () => {
    setOcrOpen(true);
    if (ocr) return;
    setOcrLoading(true);
    try {
      const r = await API.get("/firm/compliance/ocr-only");
      setOcr(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to load OCR-only list");
    } finally {
      setOcrLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await API.get("/firm/compliance/summary");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(formatApiErrorDetail(e) || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (loading) {
    return <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 sm:p-5 animate-pulse">
      <div className="h-4 w-40 bg-slate-100 rounded mb-3" />
      <div className="h-8 w-24 bg-slate-100 rounded" />
    </div>;
  }
  if (err) return null;   // silently hide for non-solicitor roles / errors
  const sh = data?.safe_harbour || {};
  const cs = data?.cases || {};
  const chipPct = Number(sh.chip_read_percent || 0);
  const barTint = chipPct >= 90 ? "#059669" : chipPct >= 70 ? "#c87d3a" : "#dc2626";
  return (
    <div className="mb-8 rounded-xl border border-slate-200 bg-white p-4 sm:p-5"
         data-testid="dashboard-safe-harbour">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            HMLR Safe Harbour — chip-read compliance
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            % of approved ID checks where the passport NFC chip (DG1 + DG2) was actually read.
            Practice Guide 8 requires 100% for the audit trail.
          </p>
        </div>
        <span className="text-[10px] uppercase font-semibold text-slate-400">
          Updated {new Date(data.generated_at).toLocaleTimeString("en-GB", {hour: "2-digit", minute: "2-digit"})}
        </span>
      </div>

      {/* Big number + progress bar */}
      <div className="flex items-baseline gap-3 mb-2">
        <span className="text-4xl font-bold" style={{ color: barTint, fontFamily: "Outfit, sans-serif" }}
              data-testid="dashboard-safe-harbour-percent">
          {chipPct.toFixed(1)}%
        </span>
        <span className="text-sm text-slate-500">
          {sh.chip_read} chip-read of {sh.approved} approved
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-4">
        <div className="h-full rounded-full transition-all duration-500"
             style={{ width: `${Math.min(100, chipPct)}%`, background: barTint }} />
      </div>

      {/* Sub-stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Stat label="Live cases" value={cs.live} testid="dashboard-safe-harbour-live" />
        <Stat label="Fully cleared" value={cs.with_all_ids_safe_harbour}
              testid="dashboard-safe-harbour-cleared"
              hint={cs.live ? `${cs.safe_harbour_case_percent}% of live` : null} />
        <Stat label="Approved (OCR only)" value={sh.ocr_only} tone="warn"
              testid="dashboard-safe-harbour-ocr" onClick={sh.ocr_only ? openOcr : undefined} />
        <Stat label="Pending / declined" value={sh.pending_or_declined} tone="muted"
              testid="dashboard-safe-harbour-pending" />
      </div>

      <Dialog open={ocrOpen} onOpenChange={setOcrOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ocr-only-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>OCR-only identity checks</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-xs text-slate-500">
              Approved by photo/OCR with no NFC chip read.
              {ocr ? <> {ocr.override_count} of {ocr.total} chose to continue without the chip.</> : null}
            </p>
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none" data-testid="ocr-only-filter-overrides">
              <Checkbox checked={onlyOverrides} onCheckedChange={(v) => setOnlyOverrides(!!v)} />
              Only client-overrides
            </label>
          </div>
          {ocrLoading ? (
            <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
          ) : !ocr || ocr.total === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="ocr-only-empty">
              No OCR-only checks — every approved ID has a chip read. 🎉
            </p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="ocr-only-list">
              {ocr.clients.filter((c) => !onlyOverrides || c.client_override).map((c, i) => (
                <li key={`${c.case_id}-${i}`} className="py-2.5 flex items-center justify-between gap-3"
                    data-testid={`ocr-only-row-${i}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                      {c.client_name}
                      {c.client_override && (
                        <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`ocr-override-badge-${i}`}>
                          Client chose to continue
                        </span>
                      )}
                      {!c.case_live && <span className="text-[10px] text-slate-400">(closed)</span>}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{c.client_email} · {c.case_ref}{c.case_title ? ` — ${c.case_title}` : ""}</p>
                  </div>
                  <Button size="sm" variant="outline" className="shrink-0 text-xs"
                    onClick={() => { setOcrOpen(false); navigate(`/cases/${c.case_id}`); }}
                    data-testid={`ocr-only-open-case-${i}`}>
                    Open case
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, hint, tone, testid, onClick }) {
  const c = tone === "warn"  ? "text-amber-700"
          : tone === "muted" ? "text-slate-400"
          :                    "text-slate-900";
  const clickable = typeof onClick === "function";
  return (
    <div className={`rounded-lg bg-slate-50 px-3 py-2 ${clickable ? "cursor-pointer hover:bg-slate-100 transition-colors" : ""}`}
         data-testid={testid} onClick={onClick} role={clickable ? "button" : undefined}>
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}{clickable && value ? " ›" : ""}</p>
      <p className={`text-lg font-semibold ${c}`}>{value ?? "—"}</p>
      {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
  );
}

// Compact onboarding-progress pill for the case list. Only renders when the
// case has onboarding activity; colour flags lagging matters (red/amber/green).
function OnbBadge({ info, testid }) {
  if (!info || info.percent === null || info.percent === undefined) return null;
  if (!info.enabled && !info.done) return null;  // no onboarding activity yet
  const p = info.percent;
  const tone = p >= 100 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : p >= 67 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : p >= 34 ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-rose-50 text-rose-700 border-rose-200";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${tone}`}
      title={`Onboarding ${info.done}/${info.total} steps complete`} data-testid={testid}>
      Onboarding {p}%
    </span>
  );
}



export default function SolicitorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Routes "/" and "/cases" both render this component (App.js). The new
  // Quick Access dashboard ("/") deliberately hides the heavy cases list
  // (tabs + search). The cases list still lives here and is rendered when
  // the user lands on "/cases" via the Cases tile.
  const isCasesRoute = location.pathname.startsWith("/cases");
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCase, setNewCase] = useState({ title: "", property_address: "", case_type: "buying", case_ref: "", client_emails: [""], buyer: { title: "", first_name: "", middle_name: "", surname: "", house_number: "", address_line1: "", address_line2: "", town: "", county: "", postcode: "" } });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");
  const [caseScope, setCaseScope] = useState("mine");
  const [finaliseItems, setFinaliseItems] = useState([]);
  // Cases-list view mode — persisted so returning to the dashboard keeps
  // the user's preferred density. "tile" = existing card grid, "list" =
  // compact single-column rows.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("cases_view_mode") || "tile"; } catch { return "tile"; }
  });
  useEffect(() => {
    try { localStorage.setItem("cases_view_mode", viewMode); } catch { /* noop */ }
  }, [viewMode]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deletedCases, setDeletedCases] = useState([]);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [caseToPermanentDelete, setCaseToPermanentDelete] = useState(null);
  const [permanentDeleting, setPermanentDeleting] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  // Lazy-load pagination state for the /cases route. Initial fetch pulls
  // `CASES_PAGE_SIZE` rows; the "Load all" button below the cases tabs
  // flips `casesLoadedAll` which re-runs the effect without a limit.
  // `casesTotal` is populated from the X-Total-Count header so the UI
  // can show "Showing 50 of 137".
  const CASES_PAGE_SIZE = 50;
  const [casesTotal, setCasesTotal] = useState(0);
  const [casesLoadedAll, setCasesLoadedAll] = useState(false);
  const [loadingMoreCases, setLoadingMoreCases] = useState(false);
  // Debounced server-side search term passed through to `/api/cases?search=`.
  const [appliedSearch, setAppliedSearch] = useState("");
  // {case_id: number_of_file_notes} — refreshed alongside the cases list
  // so each card can render a "📌 N notes" badge without an N+1 fetch.
  // Solicitor-only endpoint; clients won't see this.
  const [fileNoteCounts, setFileNoteCounts] = useState({});
  // Onboarding-progress roll-up for the visible cases (batched, 1 call).
  const [onbRollup, setOnbRollup] = useState({});

  const fetchCases = useCallback(async () => {
    try {
      const params = new URLSearchParams({ scope: caseScope });
      if (!casesLoadedAll) params.set("limit", String(CASES_PAGE_SIZE));
      if (appliedSearch) params.set("search", appliedSearch);
      const resp = await API.get(`/cases?${params.toString()}`);
      // Defensive — every render path off `cases` uses .map/.filter/.slice.
      // A non-array response (e.g. server sending an error object under
      // 200) would crash the whole dashboard tree.
      const arr = Array.isArray(resp.data) ? resp.data : [];
      setCases(arr);
      // X-Total-Count is exposed via CORS expose_headers in server.py.
      const total = Number(resp.headers?.["x-total-count"]) || arr.length;
      setCasesTotal(total);
    } catch (err) {
      console.error("Failed to fetch cases", err);
    } finally {
      setLoading(false);
      setLoadingMoreCases(false);
    }
    // Refresh file-note counts so case cards render the correct badge.
    // Solicitor-only endpoint; ignore quietly on 403 (client / referrer).
    try {
      const { data } = await API.get("/file-notes/counts");
      setFileNoteCounts(data || {});
    } catch {
      // ignore — non-solicitor or transient error
    }
  }, [caseScope, casesLoadedAll, appliedSearch]);

  // Signing requests awaiting the solicitor's date + PG82 — powers the
  // "Ready to finalise" reminder banner.
  useEffect(() => {
    let cancelled = false;
    API.get(`/signing/awaiting-finalise`, { params: { scope: caseScope } })
      .then((r) => { if (!cancelled) setFinaliseItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) setFinaliseItems([]); });
    return () => { cancelled = true; };
  }, [caseScope]);


  const handleLoadAllCases = useCallback(() => {
    setLoadingMoreCases(true);
    setCasesLoadedAll(true);
  }, []);

  // Batch-fetch onboarding completion % for the currently-loaded cases so
  // the list can flag lagging matters. One request regardless of count.
  useEffect(() => {
    const ids = (cases || []).map(c => c.id).filter(Boolean);
    if (!ids.length) { setOnbRollup({}); return undefined; }
    let cancelled = false;
    API.get(`/onboarding/progress-rollup?ids=${encodeURIComponent(ids.join(","))}`)
      .then(r => { if (!cancelled) setOnbRollup(r.data?.rollup || {}); })
      .catch(() => { /* non-solicitor or transient — no badge */ });
    return () => { cancelled = true; };
  }, [cases]);

  const fetchDeletedCases = useCallback(async () => {
    try {
      const { data } = await API.get("/cases/deleted");
      setDeletedCases(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  const [pendingInstructions, setPendingInstructions] = useState([]);
  const [instructingQuoteId, setInstructingQuoteId] = useState(null);
  // Per-row assignment selection for admin quick-assign
  const [assignTo, setAssignTo] = useState({});
  const [solicitorChoices, setSolicitorChoices] = useState([]);

  const fetchPendingInstructions = useCallback(async () => {
    try {
      const { data } = await API.get("/quotes/pending-instructions");
      setPendingInstructions(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  const fetchSolicitorChoices = useCallback(async () => {
    if (!user?.is_admin) return;
    try {
      const { data } = await API.get("/admin/users?role=solicitor");
      setSolicitorChoices((data || []).filter((u) => u.is_active !== false));
    } catch {}
  }, [user?.is_admin]);

  const handleOpenCaseFromQuote = async (quoteId) => {
    setInstructingQuoteId(quoteId);
    try {
      const assignee = assignTo[quoteId];
      const url = assignee ? `/quotes/${quoteId}/instruct?assign_to=${encodeURIComponent(assignee)}` : `/quotes/${quoteId}/instruct`;
      const { data } = await API.post(url);
      // Sale-and-purchase quotes return two cases — open the purchase leg
      // and toast about the pair so the solicitor isn't surprised.
      if (data.split && Array.isArray(data.cases) && data.cases.length > 1) {
        const purchase = data.cases.find((c) => c.transaction_leg === "purchase") || data.cases[1];
        toast.success("2 cases opened — Sale + Purchase");
        navigate(`/cases/${purchase.id}`);
      } else {
        toast.success(assignee && assignee !== user?.id ? "Case opened and assigned" : "Case opened");
        navigate(`/cases/${data.case.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to open case");
    } finally {
      setInstructingQuoteId(null);
    }
  };

  useEffect(() => {
    fetchCases();
    fetchDeletedCases();
    fetchPendingInstructions();
    fetchSolicitorChoices();
    const id = setInterval(fetchPendingInstructions, 60000);
    return () => clearInterval(id);
  }, [fetchCases, fetchDeletedCases, fetchPendingInstructions, fetchSolicitorChoices]);

  // Debounce searchQuery → appliedSearch (400ms) so we don't spam /cases on
  // every keystroke. Server-side search uses the case_ref/title/address/
  // client_name regex filter we added to GET /api/cases.
  useEffect(() => {
    const t = setTimeout(() => setAppliedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Inbox triage badge — mirrors the previous Header-level badge so the
  // new Quick Access Inbox tile still surfaces unmatched-email counts.
  useEffect(() => {
    if (user?.role !== "solicitor") { setInboxCount(0); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await API.get("/inbox/unmatched/count");
        if (!cancelled) setInboxCount(Number(data?.count) || 0);
      } catch {
        if (!cancelled) setInboxCount(0);
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.role]);

  useEffect(() => {
    if (dialogOpen) {
      API.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
    }
  }, [dialogOpen]);

  const handleCreateCase = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      const buyerFilled = Object.values(newCase.buyer || {}).some((v) => (v || "").trim());
      const payload = {
        title: newCase.title,
        property_address: newCase.property_address,
        case_type: newCase.case_type,
        case_ref: (newCase.case_ref || "").trim() || undefined,
        client_emails: newCase.client_emails.filter((e) => e.trim()),
        buyer: buyerFilled ? newCase.buyer : undefined,
        template_id: (selectedTemplateId && selectedTemplateId !== "default") ? selectedTemplateId : undefined,
      };
      await API.post("/cases", payload);
      setDialogOpen(false);
      setNewCase({ title: "", property_address: "", case_type: "buying", case_ref: "", client_emails: [""], buyer: { title: "", first_name: "", middle_name: "", surname: "", house_number: "", address_line1: "", address_line2: "", town: "", county: "", postcode: "" } });
      setSelectedTemplateId("");
      fetchCases();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const handleArchiveCase = async (e, caseId) => {
    e.stopPropagation();
    try {
      await API.put(`/cases/${caseId}/archive`);
      toast.success("Case archived");
      fetchCases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleUnarchiveCase = async (e, caseId) => {
    e.stopPropagation();
    try {
      await API.put(`/cases/${caseId}/unarchive`);
      toast.success("Case restored");
      fetchCases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDeleteCase = async () => {
    if (!caseToDelete) return;
    setDeleting(true);
    try {
      await API.delete(`/cases/${caseToDelete.id}`);
      toast.success("Case moved to Recently Deleted (30-day grace period)");
      setDeleteDialogOpen(false);
      setCaseToDelete(null);
      fetchCases();
      fetchDeletedCases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const handlePermanentDeleteCase = async () => {
    if (!caseToPermanentDelete) return;
    setPermanentDeleting(true);
    try {
      await API.delete(`/cases/${caseToPermanentDelete.id}/permanent`);
      toast.success("Case permanently deleted");
      setPermanentDeleteDialogOpen(false);
      setCaseToPermanentDelete(null);
      fetchDeletedCases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPermanentDeleting(false);
    }
  };

  const handleRestoreDeletedCase = async (e, caseId) => {
    e.stopPropagation();
    try {
      await API.put(`/cases/${caseId}/restore`);
      toast.success("Case restored");
      fetchCases();
      fetchDeletedCases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleEmptyRecentlyDeleted = async () => {
    if (deletedCases.length === 0) return;
    const confirmText = window.prompt(
      `Permanently delete ALL ${deletedCases.length} cases in Recently Deleted?\n\nThis purges every linked task, document, note, invoice and notification. This cannot be undone.\n\nType DELETE to confirm:`
    );
    if (confirmText !== "DELETE") {
      toast.info("Empty trash cancelled");
      return;
    }
    try {
      const { data } = await API.delete("/cases/deleted/empty");
      toast.success(`Permanently deleted ${data.purged} case${data.purged === 1 ? "" : "s"}`);
      fetchDeletedCases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to empty Recently Deleted");
    }
  };

  // ---------- Bulk selection state for active + trash ----------
  const [selectedActive, setSelectedActive] = useState(new Set());
  const [selectedTrash, setSelectedTrash] = useState(new Set());
  const toggleActiveCase = (id, e) => {
    if (e) e.stopPropagation();
    const next = new Set(selectedActive);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedActive(next);
  };
  const toggleTrashCase = (id, e) => {
    if (e) e.stopPropagation();
    const next = new Set(selectedTrash);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedTrash(next);
  };
  const clearActiveSel = () => setSelectedActive(new Set());
  const clearTrashSel = () => setSelectedTrash(new Set());

  const handleBulkSoftDelete = async () => {
    if (selectedActive.size === 0) return;
    if (!window.confirm(`Move ${selectedActive.size} case${selectedActive.size === 1 ? "" : "s"} to Recently Deleted?`)) return;
    try {
      const { data } = await API.post("/cases/bulk-delete", { ids: Array.from(selectedActive) });
      toast.success(`${data.moved} case${data.moved === 1 ? "" : "s"} moved to Recently Deleted`);
      clearActiveSel();
      fetchCases();
      fetchDeletedCases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk delete failed");
    }
  };

  const handleBulkRestore = async () => {
    if (selectedTrash.size === 0) return;
    try {
      const { data } = await API.post("/cases/bulk-restore", { ids: Array.from(selectedTrash) });
      toast.success(`${data.restored} case${data.restored === 1 ? "" : "s"} restored`);
      clearTrashSel();
      fetchCases();
      fetchDeletedCases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk restore failed");
    }
  };

  const handleBulkPurge = async () => {
    if (selectedTrash.size === 0) return;
    const c = window.prompt(`Permanently delete ${selectedTrash.size} selected case${selectedTrash.size === 1 ? "" : "s"}?\n\nThis cannot be undone.\n\nType DELETE to confirm:`);
    if (c !== "DELETE") return;
    try {
      const { data } = await API.post("/cases/bulk-permanent-delete", { ids: Array.from(selectedTrash) });
      toast.success(`Permanently deleted ${data.purged}`);
      clearTrashSel();
      fetchDeletedCases();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk purge failed");
    }
  };

  const openPermanentDeleteDialog = (e, caseItem) => {
    e.stopPropagation();
    setCaseToPermanentDelete(caseItem);
    setPermanentDeleteDialogOpen(true);
  };

  const openDeleteDialog = (e, caseItem) => {
    e.stopPropagation();
    setCaseToDelete(caseItem);
    setDeleteDialogOpen(true);
  };

  const activeCases = cases.filter((c) => c.status === "active");
  const completedCases = cases.filter((c) => c.status === "completed");
  const archivedCases = cases.filter((c) => c.status === "archived");
  const buyingCases = cases.filter((c) => c.case_type === "buying" && c.status !== "archived");
  const sellingCases = cases.filter((c) => c.case_type === "selling" && c.status !== "archived");

  // Search + filter
  const filteredCases = cases.filter((c) => {
    const matchesSearch = !searchQuery ||
      (c.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.property_address || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.client_name && c.client_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (c.case_ref && c.case_ref.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (c.auto_ref && c.auto_ref.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = filterType === "all" || c.case_type === filterType;
    const matchesStatus = filterStatus === "all" || c.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const getCurrentStage = (stages) => {
    const current = stages?.find((s) => s.status === "current");
    return current?.label || "Not started";
  };

  const getProgress = (stages) => {
    if (!stages) return 0;
    const completed = stages.filter((s) => s.status === "completed").length;
    return Math.round((completed / stages.length) * 100);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="solicitor-dashboard">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <OutlookReconnectBanner className="mb-6" />
        <FeeAlertBanner className="mb-6" />
        {finaliseItems.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-testid="finalise-reminder-banner">
            <div className="flex items-start gap-3">
              <Signature weight="fill" className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-blue-900">
                  {finaliseItems.length} signing request{finaliseItems.length === 1 ? "" : "s"} ready to finalise
                </p>
                <p className="text-xs text-blue-700 mt-0.5 mb-2">All parties have signed — date the deed &amp; complete the PG82 to file the signed documents.</p>
                <div className="space-y-1.5">
                  {finaliseItems.slice(0, 5).map((it) => (
                    <button
                      key={it.envelope_id}
                      onClick={() => navigate(`/cases/${it.case_id}?tab=documents&finalise=${it.envelope_id}`)}
                      data-testid={`finalise-reminder-item-${it.envelope_id}`}
                      className="w-full flex items-center justify-between gap-3 bg-white border border-blue-100 rounded-md px-3 py-2 text-left hover:border-blue-300 transition-colors"
                    >
                      <span className="min-w-0">
                        <span className="text-sm font-medium text-slate-900 truncate block">{it.title}</span>
                        <span className="text-[11px] text-slate-500">{it.case_ref}</span>
                      </span>
                      <span className="flex items-center gap-1 text-xs font-medium text-blue-700 shrink-0">Date &amp; sign PG82 <CaretRight weight="bold" className="w-3.5 h-3.5" /></span>
                    </button>
                  ))}
                  {finaliseItems.length > 5 && (
                    <p className="text-[11px] text-blue-600 pl-1">+{finaliseItems.length - 5} more</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Top bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Good {new Date().getHours() < 12 ? "morning" : "afternoon"}, {user?.name?.split(" ")[0]}
            </h1>
            <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {activeCases.length} active case{activeCases.length !== 1 ? "s" : ""} {caseScope === "all" ? "across all solicitors" : "in progress"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-white border border-slate-200 rounded-lg p-0.5" data-testid="scope-toggle">
              <button
                onClick={() => { setCaseScope("mine"); setLoading(true); }}
                data-testid="scope-mine"
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${caseScope === "mine" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
              >My Cases</button>
              <button
                onClick={() => { setCaseScope("all"); setLoading(true); }}
                data-testid="scope-all"
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${caseScope === "all" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
              >All Cases</button>
            </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-case-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white h-10 px-5"
              >
                <Plus weight="bold" className="w-4 h-4 mr-2" />
                New Case
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Create New Case</DialogTitle>
              </DialogHeader>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="create-case-error">
                  {error}
                </div>
              )}
              <form onSubmit={handleCreateCase} className="space-y-4" data-testid="create-case-form">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Case Title</Label>
                  <Input
                    data-testid="case-title-input"
                    placeholder="e.g., Smith Property Purchase"
                    value={newCase.title}
                    onChange={(e) => setNewCase({ ...newCase, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Property Address</Label>
                  <Input
                    data-testid="case-address-input"
                    placeholder="e.g., 42 King Street, London"
                    value={newCase.property_address}
                    onChange={(e) => setNewCase({ ...newCase, property_address: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Case Reference <span className="text-slate-400 text-xs">(optional)</span></Label>
                  <Input
                    data-testid="new-case-ref-input"
                    placeholder='e.g., UKC-2026-0042 or "Smith / Jones — Norwich"'
                    value={newCase.case_ref}
                    onChange={(e) => setNewCase({ ...newCase, case_ref: e.target.value })}
                    maxLength={48}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Transaction Type</Label>
                  <Select
                    value={newCase.case_type}
                    onValueChange={(v) => setNewCase({ ...newCase, case_type: v })}
                  >
                    <SelectTrigger data-testid="case-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buying">Buying</SelectItem>
                      <SelectItem value="selling">Selling</SelectItem>
                      <SelectItem value="remortgage">Remortgage</SelectItem>
                      <SelectItem value="transfer">Transfer of Equity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {templates.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Workflow Template (optional)</Label>
                    <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                      <SelectTrigger data-testid="case-template-select">
                        <SelectValue placeholder="Default workflow" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Default (6-stage UK conveyancing)</SelectItem>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name} ({t.stages?.length || 0} stages)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Client Emails (optional)</Label>
                  {newCase.client_emails.map((email, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        data-testid={`case-client-email-input-${idx}`}
                        type="email"
                        placeholder="client@example.com"
                        value={email}
                        onChange={(e) => {
                          const updated = [...newCase.client_emails];
                          updated[idx] = e.target.value;
                          setNewCase({ ...newCase, client_emails: updated });
                        }}
                      />
                      {newCase.client_emails.length > 1 && (
                        <button type="button" onClick={() => setNewCase({ ...newCase, client_emails: newCase.client_emails.filter((_, i) => i !== idx) })}
                          className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          data-testid={`remove-client-email-${idx}`}>
                          <span className="text-lg font-bold leading-none">&times;</span>
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => setNewCase({ ...newCase, client_emails: [...newCase.client_emails, ""] })}
                    data-testid="add-client-email-btn"
                    className="text-xs text-blue-600 hover:underline font-medium">
                    + Add another client
                  </button>
                </div>
                <details className="rounded-lg border border-slate-200 p-3" data-testid="new-case-buyer-details">
                  <summary className="text-sm font-medium cursor-pointer select-none text-slate-700">
                    Buyer details <span className="text-slate-400 text-xs">(optional — captured structurally for SDLT / Land Registry)</span>
                  </summary>
                  <div className="mt-3 space-y-2">
                    <div className="grid grid-cols-4 gap-2">
                      <Input placeholder="Title" value={newCase.buyer.title} onChange={(e) => setNewCase({ ...newCase, buyer: { ...newCase.buyer, title: e.target.value } })} data-testid="buyer-title-input" />
                      <Input className="col-span-1" placeholder="First name" value={newCase.buyer.first_name} onChange={(e) => setNewCase({ ...newCase, buyer: { ...newCase.buyer, first_name: e.target.value } })} data-testid="buyer-firstname-input" />
                      <Input className="col-span-1" placeholder="Middle name(s)" value={newCase.buyer.middle_name} onChange={(e) => setNewCase({ ...newCase, buyer: { ...newCase.buyer, middle_name: e.target.value } })} data-testid="buyer-middlename-input" />
                      <Input className="col-span-1" placeholder="Surname" value={newCase.buyer.surname} onChange={(e) => setNewCase({ ...newCase, buyer: { ...newCase.buyer, surname: e.target.value } })} data-testid="buyer-surname-input" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Input className="col-span-1" placeholder="Number (house/flat)" value={newCase.buyer.house_number} onChange={(e) => setNewCase({ ...newCase, buyer: { ...newCase.buyer, house_number: e.target.value } })} data-testid="buyer-housenumber-input" />
                      <Input className="col-span-2" placeholder="Address line 1" value={newCase.buyer.address_line1} onChange={(e) => setNewCase({ ...newCase, buyer: { ...newCase.buyer, address_line1: e.target.value } })} data-testid="buyer-address1-input" />
                    </div>
                    <Input placeholder="Address line 2 (optional)" value={newCase.buyer.address_line2} onChange={(e) => setNewCase({ ...newCase, buyer: { ...newCase.buyer, address_line2: e.target.value } })} data-testid="buyer-address2-input" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Town / city" value={newCase.buyer.town} onChange={(e) => setNewCase({ ...newCase, buyer: { ...newCase.buyer, town: e.target.value } })} data-testid="buyer-town-input" />
                      <Input placeholder="County (optional)" value={newCase.buyer.county} onChange={(e) => setNewCase({ ...newCase, buyer: { ...newCase.buyer, county: e.target.value } })} data-testid="buyer-county-input" />
                    </div>
                    <Input placeholder="Postcode" value={newCase.buyer.postcode} onChange={(e) => setNewCase({ ...newCase, buyer: { ...newCase.buyer, postcode: e.target.value } })} data-testid="buyer-postcode-input" />
                    <p className="text-[11px] text-slate-400">Applied to the primary client on the case. Leave blank to skip.</p>
                  </div>
                </details>
                <Button
                  type="submit"
                  data-testid="submit-case-btn"
                  disabled={creating}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                >
                  {creating ? "Creating..." : "Create Case"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Quick Access — tile-based navigation replacing the old header
            nav. Each tile is a single click into the major area of the
            app (cases, pipeline, inbox, quotes, invoices, analytics,
            referrers). Order roughly follows daily-use frequency. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-8" data-testid="dashboard-quick-access">
          {[
            { label: "Cases", desc: "All client matters", icon: FolderSimple, path: "/cases", color: "#100f5d", tint: "rgba(16,15,93,0.08)" },
            { label: "Pipeline", desc: "Kanban by stage", icon: Kanban, path: "/kanban", color: "#0891b2", tint: "rgba(8,145,178,0.10)" },
            { label: "Inbox", desc: "Triage Outlook", icon: Tray, path: "/inbox", color: "#c87d3a", tint: "rgba(200,125,58,0.12)", badge: inboxCount },
            { label: "Quotes", desc: "Create & convert", icon: Calculator, path: "/quotes", color: "#7c3aed", tint: "rgba(124,58,237,0.10)" },
            { label: "Invoices", desc: "Billing", icon: Receipt, path: "/invoices", color: "#059669", tint: "rgba(5,150,105,0.10)" },
            { label: "Accounts", desc: "Client + office ledgers", icon: Scales, path: "/accounts", color: "#0f766e", tint: "rgba(15,118,110,0.10)" },
            { label: "Analytics", desc: "Performance", icon: ChartBar, path: "/analytics", color: "#dc2626", tint: "rgba(220,38,38,0.10)" },
            { label: "Referrers", desc: "Partners", icon: Users, path: "/referrers", color: "#0284c7", tint: "rgba(2,132,199,0.10)" },
            { label: "Templates", desc: "Document merge", icon: FileText, path: "/templates", color: "#475569", tint: "rgba(71,85,105,0.10)" },
            { label: "Post-Completion", desc: "SDLT, AP1, MR01", icon: Stamp, path: "/post-completion", color: "#1462a0", tint: "rgba(20,98,160,0.10)" },
            { label: "AML / ID-V", desc: "Thirdfort dashboard", icon: ShieldCheck, path: "/aml", color: "#059669", tint: "rgba(5,150,105,0.10)" },
          ].map((tile) => (
            <button
              key={tile.path}
              onClick={() => navigate(tile.path)}
              data-testid={`tile-${tile.label.toLowerCase()}`}
              className="group relative bg-white border border-slate-200 rounded-xl p-4 sm:p-5 text-left hover:-translate-y-[2px] hover:shadow-lg hover:border-slate-300 transition-all duration-200"
            >
              <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-3 relative" style={{ background: tile.tint }}>
                <tile.icon weight="fill" className="w-5 h-5" style={{ color: tile.color }} />
                {tile.badge > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#c87d3a] text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none" data-testid={`tile-${tile.label.toLowerCase()}-badge`}>
                    {tile.badge > 9 ? "9+" : tile.badge}
                  </span>
                )}
              </div>
              <p className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{tile.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{tile.desc}</p>
              <ArrowRight weight="bold" className="w-4 h-4 text-slate-300 group-hover:text-slate-600 absolute top-4 right-4 transition-colors" />
            </button>
          ))}
        </div>

        {/* Hidden on the case list page per user request; still shown on the main dashboard. */}
        {!isCasesRoute && <SafeHarbourTile />}

        {/* Overdue Tasks and SDLT deadline widgets removed from the
            dashboard on user request (iteration 71) — the info still
            surfaces on the case page itself and in the tasks list. */}

        {/* Overdue Tasks, SDLT, HMLR Outstanding Requests, and AML
            Analytics widgets removed from the dashboard on user request
            (iterations 71–72) — the info still surfaces on the case
            page itself and via the dedicated Post-Completion / AML
            quick-access tiles. */}

        {/* HMLR eDRS filings widget removed from the dashboard on user
            request. eDRS is being retired in favour of HMLR NRS anyway,
            and the info still surfaces on the case page itself + the
            Post-Completion tile. */}

        {/* Pending Instructions widget — referrer/client has clicked "Request to Instruct" */}
        {pendingInstructions.length > 0 && (
          <div className="mb-8 bg-white rounded-lg p-5 shadow-sm" style={{ borderWidth: "2px", borderStyle: "solid", borderColor: "rgba(200,125,58,0.35)" }} data-testid="pending-instructions-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(200,125,58,0.15)" }}>
                  <Handshake weight="fill" className="w-4 h-4" style={{ color: "#c87d3a" }} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Pending Instructions ({pendingInstructions.length})
                  </h3>
                  <p className="text-xs text-slate-500">Quotes where a client or referrer has requested instruction — click to open the case.</p>
                </div>
              </div>
              <Button onClick={fetchPendingInstructions} variant="outline" size="sm" className="h-8 px-3 text-xs" data-testid="refresh-pending-btn">
                <ArrowClockwise weight="bold" className="w-3 h-3 mr-1.5" /> Refresh
              </Button>
            </div>
            <div className="space-y-2">
              {pendingInstructions.slice(0, 8).map((q) => {
                const isReferrerLead = (q.source === "referrer" || q.source === "referrer_bespoke") && q.referrer_name;
                return (
                  <div key={q.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-amber-50/30 transition-colors" style={{ borderColor: "rgba(200,125,58,0.2)" }} data-testid={`pending-instruction-${q.id}`}>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(16,15,93,0.08)" }}>
                      <User weight="bold" className="w-4 h-4" style={{ color: "#100f5d" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900 truncate">{q.client_name}</p>
                        {isReferrerLead ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: "rgba(200,125,58,0.15)", color: "#c87d3a" }}>
                            via {q.referrer_name}
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Direct</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                        {q.property_address && <span className="truncate max-w-[280px]">{q.property_address}</span>}
                        {q.property_address && <span>·</span>}
                        <span>{{ buying: "Buying", selling: "Selling", both: "Buying & Selling", remortgage: "Remortgage", transfer: "Transfer of Equity", grant_of_lease: "Grant of Lease" }[q.transaction_type] || q.transaction_type}</span>
                        <span>·</span>
                        <span>Requested {q.client_instructed_at ? new Date(q.client_instructed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}</span>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-slate-900 shrink-0 mr-2">
                      {new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(q.total || 0)}
                    </p>
                    {user?.is_admin && solicitorChoices.length > 1 && (
                      <select
                        value={assignTo[q.id] || user.id}
                        onChange={(e) => setAssignTo((prev) => ({ ...prev, [q.id]: e.target.value }))}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`assign-to-select-${q.id}`}
                        className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#100f5d]/30 min-w-[150px]"
                      >
                        {solicitorChoices.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.id === user.id ? `Me (${s.name.split(" ")[0]})` : s.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <Button onClick={() => window.open(`/quote/view/${q.id}`, "_blank")} variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid={`view-quote-${q.id}`}>
                      View
                    </Button>
                    <Button onClick={() => handleOpenCaseFromQuote(q.id)} disabled={instructingQuoteId === q.id} size="sm" className="h-8 text-xs text-white hover:opacity-90 gap-1.5" style={{ background: "#100f5d" }} data-testid={`open-case-from-quote-${q.id}`}>
                      {instructingQuoteId === q.id ? "Opening…" : <><ArrowRight weight="bold" className="w-3.5 h-3.5" /> Open Case</>}
                    </Button>
                  </div>
                );
              })}
              {pendingInstructions.length > 8 && (
                <p className="text-xs text-slate-500 text-center pt-1">
                  +{pendingInstructions.length - 8} more pending instructions — <button className="underline hover:text-slate-700" onClick={() => navigate("/quotes")}>view all</button>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Search & Filter — cases list only renders on /cases route */}
        {isCasesRoute && (
        <>
        <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="search-filter-section">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="case-search-input"
              placeholder="Search by title, address, client, or ref..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 bg-white border-slate-200"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-36 h-10 bg-white border-slate-200" data-testid="filter-type-select">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="buying">Buying</SelectItem>
              <SelectItem value="selling">Selling</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-36 h-10 bg-white border-slate-200" data-testid="filter-status-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full sm:w-44 h-10 bg-white border-slate-200" data-testid="sort-select">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date_desc" data-testid="sort-date-desc">Date opened — newest</SelectItem>
              <SelectItem value="date_asc" data-testid="sort-date-asc">Date opened — oldest</SelectItem>
              <SelectItem value="progress_desc" data-testid="sort-progress-desc">Progress — most complete</SelectItem>
              <SelectItem value="progress_asc" data-testid="sort-progress-asc">Progress — least complete</SelectItem>
              <SelectItem value="client_asc" data-testid="sort-client-asc">Client name — A to Z</SelectItem>
            </SelectContent>
          </Select>
          {(searchQuery || filterType !== "all" || filterStatus !== "all") && (
            <Button
              variant="ghost"
              data-testid="clear-filters-btn"
              onClick={() => { setSearchQuery(""); setFilterType("all"); setFilterStatus("all"); }}
              className="h-10 px-3 text-sm text-slate-500 hover:text-slate-900"
            >
              Clear
            </Button>
          )}
        </div>

        {/* Results count + view-mode toggle */}
        <div className="flex items-center justify-between mb-3 gap-3">
          <p className="text-xs text-slate-500" data-testid="search-results-count">
            {(searchQuery || filterType !== "all" || filterStatus !== "all")
              ? `Showing ${filteredCases.length} of ${cases.length} cases`
              : `${cases.length} case${cases.length === 1 ? "" : "s"}`}
          </p>
          <div className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5" data-testid="cases-view-toggle">
            <button
              type="button"
              onClick={() => setViewMode("tile")}
              className={`px-2 py-1 rounded-[5px] text-xs font-medium flex items-center gap-1.5 transition-colors ${viewMode === "tile" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
              data-testid="cases-view-tile-btn"
              title="Tile view"
              aria-pressed={viewMode === "tile"}
            >
              <SquaresFour weight="bold" className="w-3.5 h-3.5" /> Tiles
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`px-2 py-1 rounded-[5px] text-xs font-medium flex items-center gap-1.5 transition-colors ${viewMode === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
              data-testid="cases-view-list-btn"
              title="List view"
              aria-pressed={viewMode === "list"}
            >
              <Rows weight="bold" className="w-3.5 h-3.5" /> List
            </button>
          </div>
        </div>

        {/* Cases tabs */}
        <Tabs defaultValue="active" className="space-y-4">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="active" data-testid="tab-active">Active ({activeCases.length})</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">Completed ({completedCases.length})</TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived">Archived ({archivedCases.length})</TabsTrigger>
            {deletedCases.length > 0 && (
              <TabsTrigger value="deleted" data-testid="tab-deleted" className="gap-1 text-red-600">
                Recently Deleted ({deletedCases.length})
              </TabsTrigger>
            )}
            <TabsTrigger value="all" data-testid="tab-all">All ({cases.length})</TabsTrigger>
          </TabsList>

          {["active", "completed", "archived", "all"].map((tab) => {
            const tabBase = tab === "active" ? activeCases : tab === "completed" ? completedCases : tab === "archived" ? archivedCases : cases;
            const filtered = tabBase.filter((c) => {
              const q = searchQuery.toLowerCase();
              const matchesSearch = !searchQuery ||
                (c.title || "").toLowerCase().includes(q) ||
                (c.property_address || "").toLowerCase().includes(q) ||
                (c.client_name && c.client_name.toLowerCase().includes(q)) ||
                (c.case_ref && c.case_ref.toLowerCase().includes(q)) ||
                (c.auto_ref && c.auto_ref.toLowerCase().includes(q));
              const matchesType = filterType === "all" || c.case_type === filterType;
              return matchesSearch && matchesType;
            }).sort((a, b) => {
              if (sortBy === "progress_desc" || sortBy === "progress_asc") {
                const pa = getProgress(a.stages);
                const pb = getProgress(b.stages);
                return sortBy === "progress_asc" ? pa - pb : pb - pa;
              }
              if (sortBy === "client_asc") {
                return (a.client_name || "").localeCompare(b.client_name || "", "en", { sensitivity: "base" });
              }
              const da = a.created_at ? new Date(a.created_at).getTime() : 0;
              const db2 = b.created_at ? new Date(b.created_at).getTime() : 0;
              return sortBy === "date_asc" ? da - db2 : db2 - da;
            });
            return (
              <TabsContent key={tab} value={tab}>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-12 bg-white border border-slate-200 rounded-lg" data-testid="empty-cases">
                    <FolderSimple weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500 font-medium">No {tab === "all" ? "" : tab} cases yet</p>
                    {tab !== "completed" && (
                      <Button
                        variant="link"
                        onClick={() => setDialogOpen(true)}
                        className="mt-2 text-blue-600"
                      >
                        Create your first case
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <BulkActionBar
                      count={[...selectedActive].filter((id) => filtered.some((f) => f.id === id)).length}
                      onClear={clearActiveSel}
                      onDelete={handleBulkSoftDelete}
                      label="case"
                      variant="active"
                      testIdPrefix="cases-active"
                    />
                    <div className={viewMode === "list" ? "flex flex-col gap-2" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"}>
                    {filtered.map((c) => {
                      const isSelected = selectedActive.has(c.id);
                      // Cases list rows open the case file in a NEW BROWSER TAB
                      // so solicitors can work on several cases side-by-side
                      // (per user request — only the main `/cases` list does
                      // this; everywhere else stays in-tab).
                      const openInNewTab = () => {
                        window.open(`/cases/${c.id}`, "_blank", "noopener,noreferrer");
                      };
                      if (viewMode === "list") {
                        return (
                          <div
                            key={c.id}
                            onClick={openInNewTab}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === "Enter" && openInNewTab()}
                            data-testid={`case-row-${c.id}`}
                            className={`cursor-pointer bg-white border rounded-lg px-3 py-2.5 shadow-sm hover:border-slate-300 hover:shadow transition-all duration-150 group flex items-center gap-3 ${isSelected ? "border-[#100f5d] ring-2 ring-[#100f5d]/15" : "border-slate-200"}`}
                          >
                            <div
                              onClick={(e) => toggleActiveCase(c.id, e)}
                              data-testid={`case-checkbox-${c.id}`}
                              className="shrink-0"
                            >
                              <Checkbox checked={isSelected} onCheckedChange={() => toggleActiveCase(c.id)} onClick={(e) => e.stopPropagation()} />
                            </div>
                            {/* Title + address */}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-sm font-semibold text-slate-900 truncate group-hover:text-blue-600 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                  {c.title}
                                </h3>
                                {c.case_ref && (
                                  <span className="text-[10px] font-mono text-slate-400" data-testid={`case-list-ref-${c.id}`}>#{c.case_ref}</span>
                                )}
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${c.case_type === "buying" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                                  {c.case_type}
                                </span>
                                <OnbBadge info={onbRollup[c.id]} testid={`case-onb-badge-${c.id}`} />
                                {fileNoteCounts[c.id] > 0 && (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100" title={`${fileNoteCounts[c.id]} internal file note${fileNoteCounts[c.id] === 1 ? "" : "s"}`} data-testid={`case-notes-badge-${c.id}`}>
                                    <NotePencil weight="bold" className="w-2.5 h-2.5" />{fileNoteCounts[c.id]}
                                  </span>
                                )}
                                {c.status === "archived" && (
                                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-500" data-testid={`archived-badge-${c.id}`}>Archived</span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 truncate mt-0.5">
                                {c.property_address}
                                {(c.clients?.length > 0 || c.client_name) && (
                                  <span className="text-slate-400"> · {c.clients?.length > 0 ? c.clients.map(cl => cl.name).join(", ") : c.client_name}</span>
                                )}
                                {caseScope === "all" && c.solicitor_name && (
                                  <span className="text-blue-600 font-medium"> · {c.solicitor_name}</span>
                                )}
                              </p>
                            </div>
                            {/* Progress bar */}
                            <div className="hidden md:flex items-center gap-2 w-56 shrink-0">
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-[10px] text-slate-500 flex items-center gap-1 truncate">
                                    <Clock weight="bold" className="w-2.5 h-2.5" /> {getCurrentStage(c.stages)}
                                  </span>
                                  <span className="text-[10px] font-medium text-slate-900">{getProgress(c.stages)}%</span>
                                </div>
                                <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${getProgress(c.stages)}%` }} />
                                </div>
                              </div>
                            </div>
                            {/* Date opened */}
                            <div className="hidden lg:flex flex-col items-end shrink-0 w-24" data-testid={`case-opened-${c.id}`}>
                              <span className="text-[9px] uppercase tracking-wide text-slate-400">Opened</span>
                              <span className="text-[11px] font-medium text-slate-600">{c.created_at ? new Date(c.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</span>
                            </div>
                            {/* Actions */}
                            <div className="flex items-center gap-0.5 shrink-0">
                              {c.status === "archived" ? (
                                <button onClick={(e) => handleUnarchiveCase(e, c.id)} data-testid={`unarchive-case-${c.id}`}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-emerald-100 text-slate-400 hover:text-emerald-600 transition-colors"
                                  title="Restore case">
                                  <Archive weight="bold" className="w-3.5 h-3.5" />
                                </button>
                              ) : (
                                <button onClick={(e) => handleArchiveCase(e, c.id)} data-testid={`archive-case-${c.id}`}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-amber-100 text-slate-400 hover:text-amber-600 transition-colors"
                                  title="Archive case">
                                  <Archive weight="bold" className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button onClick={(e) => openDeleteDialog(e, c)} data-testid={`delete-case-${c.id}`}
                                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors"
                                title="Delete case">
                                <Trash weight="bold" className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      }
                      return (
                      <div
                        key={c.id}
                        onClick={openInNewTab}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && openInNewTab()}
                        data-testid={`case-card-${c.id}`}
                        className={`cursor-pointer text-left bg-white border rounded-lg p-5 shadow-sm hover:-translate-y-[2px] hover:shadow-md transition-all duration-200 group relative ${isSelected ? "border-[#100f5d] ring-2 ring-[#100f5d]/15" : "border-slate-200"}`}
                      >
                        <div
                          onClick={(e) => toggleActiveCase(c.id, e)}
                          className="absolute top-3 left-3 z-10"
                          data-testid={`case-checkbox-${c.id}`}
                        >
                          <Checkbox checked={isSelected} onCheckedChange={() => toggleActiveCase(c.id)} onClick={(e) => e.stopPropagation()} />
                        </div>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h3
                              className="text-sm font-semibold text-slate-900 truncate group-hover:text-blue-600 transition-colors"
                              style={{ fontFamily: 'Outfit, sans-serif' }}
                            >
                              {c.title}
                            </h3>
                            <p className="text-xs text-slate-500 mt-0.5 truncate">{c.property_address}</p>
                            {c.case_ref && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-500 mt-1" data-testid={`case-list-ref-${c.id}`}>
                                <span className="text-slate-400">#</span>{c.case_ref}
                              </span>
                            )}
                            <p className="text-[10px] text-slate-400 mt-1" data-testid={`case-opened-grid-${c.id}`}>
                              Opened {c.created_at ? new Date(c.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                            </p>
                          </div>
                          <span
                            className={`ml-2 shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                              c.case_type === "buying" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"
                            }`}
                          >
                            {c.case_type}
                          </span>
                          <span className="ml-1"><OnbBadge info={onbRollup[c.id]} testid={`case-onb-badge-grid-${c.id}`} /></span>
                          {fileNoteCounts[c.id] > 0 && (
                            <span
                              className="ml-1 shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100"
                              title={`${fileNoteCounts[c.id]} internal file note${fileNoteCounts[c.id] === 1 ? "" : "s"}`}
                              data-testid={`case-notes-badge-${c.id}`}
                            >
                              <NotePencil weight="bold" className="w-2.5 h-2.5" />
                              {fileNoteCounts[c.id]}
                            </span>
                          )}
                          {c.status === "archived" && (
                            <span className="ml-1 shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-200 text-slate-500" data-testid={`archived-badge-${c.id}`}>
                              Archived
                            </span>
                          )}
                        </div>

                        {/* Progress */}
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock weight="bold" className="w-3 h-3" />
                              {getCurrentStage(c.stages)}
                            </span>
                            <span className="text-xs font-medium text-slate-900">{getProgress(c.stages)}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-600 rounded-full transition-all duration-500"
                              style={{ width: `${getProgress(c.stages)}%` }}
                            />
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            {caseScope === "all" && c.solicitor_name && (
                              <p className="text-[10px] text-blue-600 font-medium mb-0.5 truncate">Solicitor: {c.solicitor_name}</p>
                            )}
                            {(c.clients?.length > 0 || c.client_name) ? (
                              <span className="text-xs text-slate-500 truncate block">
                                {c.clients?.length > 0
                                  ? `Client${c.clients.length > 1 ? "s" : ""}: ${c.clients.map(cl => cl.name).join(", ")}`
                                  : `Client: ${c.client_name}`
                                }
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400 italic">No client assigned</span>
                            )}
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            {c.status === "archived" ? (
                              <button onClick={(e) => handleUnarchiveCase(e, c.id)} data-testid={`unarchive-case-${c.id}`}
                                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-emerald-100 text-slate-400 hover:text-emerald-600 transition-colors"
                                title="Restore case">
                                <Archive weight="bold" className="w-3.5 h-3.5" />
                              </button>
                            ) : (
                              <button onClick={(e) => handleArchiveCase(e, c.id)} data-testid={`archive-case-${c.id}`}
                                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-amber-100 text-slate-400 hover:text-amber-600 transition-colors"
                                title="Archive case">
                                <Archive weight="bold" className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button onClick={(e) => openDeleteDialog(e, c)} data-testid={`delete-case-${c.id}`}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors"
                              title="Delete case">
                              <Trash weight="bold" className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  </>
                )}
              </TabsContent>
            );
          })}

          {/* Recently Deleted Tab */}
          <TabsContent value="deleted">
            {deletedCases.length === 0 ? (
              <div className="text-center py-12 bg-white border border-slate-200 rounded-lg" data-testid="empty-deleted">
                <Trash weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 font-medium">No recently deleted cases</p>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <p className="text-xs text-slate-500 font-medium">
                    Cases are automatically permanently deleted after 30 days
                  </p>
                  {user?.is_admin && (
                    <Button
                      onClick={handleEmptyRecentlyDeleted}
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs gap-1.5 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                      data-testid="empty-recently-deleted-btn"
                    >
                      <Trash weight="bold" className="w-3.5 h-3.5" /> Empty Recently Deleted ({deletedCases.length})
                    </Button>
                  )}
                </div>
                <BulkActionBar
                  count={selectedTrash.size}
                  onClear={clearTrashSel}
                  onRestore={handleBulkRestore}
                  onPurge={user?.is_admin ? handleBulkPurge : undefined}
                  label="case"
                  variant="trash"
                  testIdPrefix="cases-trash"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {deletedCases.map((c) => {
                    const isSelected = selectedTrash.has(c.id);
                    return (
                    <div key={c.id} data-testid={`deleted-card-${c.id}`}
                      className={`relative bg-white border rounded-lg p-5 shadow-sm transition-all ${isSelected ? "border-[#100f5d] ring-2 ring-[#100f5d]/15" : "border-red-100 opacity-75 hover:opacity-100"}`}>
                      <div onClick={(e) => toggleTrashCase(c.id, e)} className="absolute top-3 left-3 z-10" data-testid={`trash-checkbox-${c.id}`}>
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleTrashCase(c.id)} />
                      </div>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.title}</h3>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{c.property_address}</p>
                        </div>
                        <span className={`ml-2 shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                          c.days_remaining <= 7 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                        }`} data-testid={`days-remaining-${c.id}`}>
                          {c.days_remaining} day{c.days_remaining !== 1 ? "s" : ""} left
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <p className="text-xs text-slate-400">
                          Deleted {c.deleted_at ? new Date(c.deleted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <button onClick={(e) => handleRestoreDeletedCase(e, c.id)} data-testid={`restore-deleted-${c.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors">
                            <Archive weight="bold" className="w-3 h-3" /> Restore
                          </button>
                          <button onClick={(e) => openPermanentDeleteDialog(e, c)} data-testid={`permanent-delete-${c.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors">
                            <Trash weight="bold" className="w-3 h-3" /> Delete Forever
                          </button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Lazy-load paginator — only when the dataset exceeds the initial
            page size. Type/status filters above are client-side so once the
            user clicks "Load all" they get true cross-tab filtering. */}
        {!casesLoadedAll && casesTotal > cases.length && (
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-white border border-slate-200 rounded-lg" data-testid="cases-load-more-banner">
            <p className="text-xs text-slate-600">
              Showing <span className="font-semibold text-slate-900">{cases.length}</span> of <span className="font-semibold text-slate-900">{casesTotal}</span> cases. Type & status filters apply only to loaded cases.
            </p>
            <Button
              size="sm"
              variant="outline"
              data-testid="cases-load-all-btn"
              onClick={handleLoadAllCases}
              disabled={loadingMoreCases}
              className="h-8 text-xs"
            >
              {loadingMoreCases ? (
                <>
                  <ArrowClockwise className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Loading…
                </>
              ) : (
                <>Load all {casesTotal - cases.length} remaining</>
              )}
            </Button>
          </div>
        )}
        </>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Delete Case</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700 font-medium flex items-center gap-1.5">
                  <Warning weight="fill" className="w-4 h-4" /> Case will be moved to Recently Deleted
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  You'll have 30 days to restore it before it's permanently removed.
                </p>
              </div>
              {caseToDelete && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="text-sm font-medium text-slate-900">{caseToDelete.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{caseToDelete.property_address}</p>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="flex-1" data-testid="cancel-delete-btn">
                  Cancel
                </Button>
                <Button onClick={handleDeleteCase} disabled={deleting}
                  data-testid="confirm-delete-case-btn"
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                  {deleting ? "Deleting..." : "Delete Case"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Permanent Delete Confirmation Dialog */}
        <Dialog open={permanentDeleteDialogOpen} onOpenChange={setPermanentDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Delete Permanently</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 font-medium flex items-center gap-1.5">
                  <Warning weight="fill" className="w-4 h-4" /> This action cannot be undone
                </p>
                <p className="text-xs text-red-600 mt-1">
                  All documents, tasks, notes, and notifications will be permanently removed.
                </p>
              </div>
              {caseToPermanentDelete && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="text-sm font-medium text-slate-900">{caseToPermanentDelete.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{caseToPermanentDelete.property_address}</p>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setPermanentDeleteDialogOpen(false)} className="flex-1" data-testid="cancel-permanent-delete-btn">
                  Cancel
                </Button>
                <Button onClick={handlePermanentDeleteCase} disabled={permanentDeleting}
                  data-testid="confirm-permanent-delete-btn"
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                  {permanentDeleting ? "Deleting..." : "Delete Forever"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
